import { useCallback, useEffect, useMemo, useState } from "react";
import {
  acknowledgeDispensaryPosOrderRefusal,
  confirmDispensaryOrder,
  fetchDispensaryOrderDetail,
  fetchDispensaryOrders,
  fetchDispensaryPosOrderRefusals,
  fetchOrgOrders,
  retryDispensaryPosOrderRefusal,
  type DispensaryOrderDetailResponse,
  type DispensaryOrderListItem,
  type DispensaryPosOrderRefusal,
} from "../lib/dispensary-delivery-api";
import { formatCentsAsUsd } from "../lib/format-usd";

/** ADR-125 — shown where an amount due would otherwise appear. */
const PREPAID_VIA_PARTNER_LABEL = "Prepaid via partner";
import { openSalesReceiptPdf } from "../lib/open-sales-receipt-pdf";
import { useDispensaryWorkspace } from "../workspace/dispensary-workspace-context";

/** Queue row — base list item, plus per-location info present only in "All locations" mode. */
type QueueOrderItem = DispensaryOrderListItem & {
  dispensaryId?: string;
  locationLabel?: string | null;
  locationName?: string;
};

function coloradoDateFromIso(iso: string): string {
  return new Date(iso).toLocaleDateString("en-CA", { timeZone: "America/Denver" });
}

function coloradoToday(): string {
  return new Date().toLocaleDateString("en-CA", { timeZone: "America/Denver" });
}

function bucketOrders(orders: QueueOrderItem[]): {
  awaitingConfirmation: QueueOrderItem[];
  beingPrepared: QueueOrderItem[];
  outForDelivery: QueueOrderItem[];
  completedToday: QueueOrderItem[];
  failedToday: QueueOrderItem[];
} {
  const today = coloradoToday();
  const awaitingConfirmation: QueueOrderItem[] = [];
  const beingPrepared: QueueOrderItem[] = [];
  const outForDelivery: QueueOrderItem[] = [];
  const completedToday: QueueOrderItem[] = [];
  const failedToday: QueueOrderItem[] = [];

  for (const o of orders) {
    const s = o.status;
    if (s === "pending") {
      awaitingConfirmation.push(o);
    } else if (s === "confirmed") {
      beingPrepared.push(o);
    } else if (s === "dispatched" || s === "in_progress") {
      outForDelivery.push(o);
    } else if (s === "completed" && coloradoDateFromIso(o.updatedAt) === today) {
      completedToday.push(o);
    } else if ((s === "failed" || s === "cancelled") && coloradoDateFromIso(o.updatedAt) === today) {
      failedToday.push(o);
    }
  }
  return { awaitingConfirmation, beingPrepared, outForDelivery, completedToday, failedToday };
}

export function OrdersQueueScreen() {
  const { dispensaryId, orgId, allLocations, getToken } = useDispensaryWorkspace();
  const [orders, setOrders] = useState<QueueOrderItem[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  // In "All locations" mode each row carries its own store; detail/confirm
  // target that store, not the (null) workspace one.
  const [detailDispensaryId, setDetailDispensaryId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DispensaryOrderDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirmNotes, setConfirmNotes] = useState("");
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmErr, setConfirmErr] = useState<string | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showFailed, setShowFailed] = useState(false);
  // ADR-130 — storefront orders Drop refused to import; per store only.
  const [refusals, setRefusals] = useState<DispensaryPosOrderRefusal[]>([]);
  const [refusalBusyId, setRefusalBusyId] = useState<string | null>(null);
  const [refusalErr, setRefusalErr] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      if (allLocations && orgId) {
        const r = await fetchOrgOrders(getToken, orgId, { limit: 150 });
        setOrders(r.orders);
        setRefusals([]);
      } else if (dispensaryId) {
        const [r, refused] = await Promise.all([
          fetchDispensaryOrders(getToken, dispensaryId, { limit: 150 }),
          fetchDispensaryPosOrderRefusals(getToken, dispensaryId).catch(() => ({ refusals: [] })),
        ]);
        setOrders(r.orders);
        setRefusals(refused.refusals);
      } else {
        setOrders([]);
        setRefusals([]);
      }
    } catch {
      setLoadError("Could not load orders.");
    } finally {
      setLoading(false);
    }
  }, [allLocations, orgId, dispensaryId, getToken]);

  const onRefusalAction = useCallback(
    async (refusalId: string, action: "acknowledge" | "retry") => {
      if (!dispensaryId) return;
      setRefusalBusyId(refusalId);
      setRefusalErr(null);
      try {
        const r =
          action === "acknowledge"
            ? await acknowledgeDispensaryPosOrderRefusal(getToken, dispensaryId, refusalId)
            : await retryDispensaryPosOrderRefusal(getToken, dispensaryId, refusalId);
        setRefusals((prev) =>
          action === "acknowledge"
            ? prev.filter((x) => x.id !== refusalId)
            : prev.map((x) => (x.id === refusalId ? r.refusal : x)),
        );
      } catch {
        setRefusalErr(action === "acknowledge" ? "Could not mark as handled." : "Could not request a retry.");
      } finally {
        setRefusalBusyId(null);
      }
    },
    [dispensaryId, getToken],
  );

  // Resolve which store a selected order belongs to (its own, in "all" mode).
  const onSelectOrder = useCallback(
    (o: QueueOrderItem) => {
      setDetailId(o.id);
      setDetailDispensaryId(o.dispensaryId ?? dispensaryId);
    },
    [dispensaryId],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const buckets = useMemo(() => bucketOrders(orders), [orders]);

  useEffect(() => {
    if (!detailId || !detailDispensaryId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setConfirmErr(null);
    void (async () => {
      try {
        const d = await fetchDispensaryOrderDetail(getToken, detailDispensaryId, detailId);
        if (!cancelled) setDetail(d);
      } catch {
        if (!cancelled) setDetail(null);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detailId, detailDispensaryId, getToken]);

  async function onConfirm() {
    if (!detailDispensaryId || !detailId || !detail) return;
    setConfirmBusy(true);
    setConfirmErr(null);
    try {
      await confirmDispensaryOrder(getToken, detailDispensaryId, detailId, confirmNotes.trim() || null);
      setDetailId(null);
      setConfirmNotes("");
      await refresh();
    } catch (e: unknown) {
      const body =
        e && typeof e === "object" && "body" in e
          ? (e as { body?: { error?: string } }).body
          : undefined;
      setConfirmErr(body?.error ?? "Confirm failed.");
    } finally {
      setConfirmBusy(false);
    }
  }

  if (!dispensaryId && !(allLocations && orgId)) {
    return <p className="text-sm text-amber-200/90">Set dispensary scope (admin) or link your account to a shop.</p>;
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-ink">Orders</h1>
          <p className="mt-1 text-sm text-gray-500">
            {allLocations
              ? "All locations · orders across your stores, grouped by stage."
              : "Orders grouped by confirmation and dispatch stage."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={loading}
          className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:border-green-hover disabled:opacity-50"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {loadError ? <p className="text-sm text-red-300">{loadError}</p> : null}

      {refusals.length > 0 ? (
        <RefusedPosOrdersPanel
          refusals={refusals}
          busyId={refusalBusyId}
          error={refusalErr}
          onAction={(id, action) => void onRefusalAction(id, action)}
        />
      ) : null}

      <Swimlane title="Awaiting confirmation" tone="red" orders={buckets.awaitingConfirmation} onSelect={onSelectOrder} />
      <Swimlane title="Being prepared" tone="amber" orders={buckets.beingPrepared} onSelect={onSelectOrder} />
      <Swimlane title="Out for delivery" tone="blue" orders={buckets.outForDelivery} onSelect={onSelectOrder} />

      <section className="rounded-lg border border-gray-200 bg-white">
        <button
          type="button"
          onClick={() => setShowCompleted((v) => !v)}
          className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium text-gray-700"
        >
          Completed today ({buckets.completedToday.length})
          <span className="text-gray-500">{showCompleted ? "▼" : "▶"}</span>
        </button>
        {showCompleted ? (
          <div className="border-t border-gray-200 px-4 pb-4 pt-2">
            <Swimlane title="" tone="green" orders={buckets.completedToday} onSelect={onSelectOrder} bare />
          </div>
        ) : null}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white">
        <button
          type="button"
          onClick={() => setShowFailed((v) => !v)}
          className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium text-gray-700"
        >
          Failed / cancelled today ({buckets.failedToday.length})
          <span className="text-gray-500">{showFailed ? "▼" : "▶"}</span>
        </button>
        {showFailed ? (
          <div className="border-t border-gray-200 px-4 pb-4 pt-2">
            <Swimlane title="" tone="red" orders={buckets.failedToday} onSelect={onSelectOrder} bare />
          </div>
        ) : null}
      </section>

      {detailId ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" role="dialog">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-gray-200 bg-white p-6">
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-lg font-semibold text-ink">Order detail</h2>
              <button
                type="button"
                className="text-sm text-gray-500 hover:text-gray-700"
                onClick={() => setDetailId(null)}
              >
                Close
              </button>
            </div>
            {detailLoading ? <p className="mt-4 text-sm text-gray-500">Loading…</p> : null}
            {detail && !detailLoading ? (
              <OrderDetailBody
                detail={detail}
                getToken={getToken}
                confirmNotes={confirmNotes}
                setConfirmNotes={setConfirmNotes}
                onConfirm={detail.order.status === "pending" ? () => void onConfirm() : undefined}
                confirmBusy={confirmBusy}
                confirmErr={confirmErr}
              />
            ) : null}
            {!detail && !detailLoading ? <p className="mt-4 text-sm text-red">Could not load order.</p> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function posDisplayName(posAdapter: string): string {
  return posAdapter === "dutchie" ? "Dutchie" : posAdapter.charAt(0).toUpperCase() + posAdapter.slice(1);
}

function refusalTimeLabel(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    timeZone: "America/Denver",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * ADR-130 — storefront orders Drop could not import. Each row says why, what
 * Drop already did in the point of sale, and what staff still need to do:
 * cancelled there already → nothing but a glance; otherwise reject it there
 * (the order will not be delivered by Drop) or, when the cause is on Drop's
 * side, fix it and ask for a retry.
 */
function RefusedPosOrdersPanel({
  refusals,
  busyId,
  error,
  onAction,
}: {
  refusals: DispensaryPosOrderRefusal[];
  busyId: string | null;
  error: string | null;
  onAction: (refusalId: string, action: "acknowledge" | "retry") => void;
}) {
  return (
    <section className="rounded-lg border border-red-200 bg-red-50/60">
      <div className="border-b border-red-200 px-4 py-3">
        <h2 className="text-sm font-semibold text-red-800">Storefront orders not imported ({refusals.length})</h2>
        <p className="mt-1 text-xs text-red-700/90">
          Drop did not take these orders into delivery. Where Drop could not cancel the order in your point of sale,
          reject it there (or fix the cause and retry the import), then mark it handled.
        </p>
      </div>
      {error ? <p className="px-4 pt-3 text-sm text-red-700">{error}</p> : null}
      <ul className="divide-y divide-red-100">
        {refusals.map((r) => {
          const pos = posDisplayName(r.posAdapter);
          const busy = busyId === r.id;
          return (
            <li key={r.id} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-ink">
                  {r.customerName ?? "Unknown customer"}
                  <span className="ml-2 font-normal text-gray-500">
                    {pos} order {r.posOrderId}
                  </span>
                </p>
                <p className="mt-0.5 text-sm text-red-800">{r.refusalLabel}</p>
                <p className="mt-0.5 text-xs text-gray-600">
                  {r.posAction === "cancelled_in_pos"
                    ? `Cancelled in ${pos} by Drop.`
                    : r.staffFixable
                      ? `Not cancelled in ${pos}. Fix the cause in Drop and retry the import, or reject it in ${pos}.`
                      : `Not cancelled in ${pos} (the order was already being filled). Reject it there.`}
                  {" · "}
                  {refusalTimeLabel(r.refusedAt)}
                  {r.attemptCount > 1 ? ` · refused ${r.attemptCount} times` : ""}
                  {r.retryRequestedAt ? " · retry pending" : ""}
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                {r.staffFixable ? (
                  <button
                    type="button"
                    disabled={busy || r.retryRequestedAt != null}
                    onClick={() => onAction(r.id, "retry")}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:border-green-hover disabled:opacity-50"
                  >
                    {r.retryRequestedAt ? "Retry pending" : "Retry import"}
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onAction(r.id, "acknowledge")}
                  className="rounded-md bg-ink px-3 py-1.5 text-xs text-white hover:opacity-90 disabled:opacity-50"
                >
                  {busy ? "Saving…" : "Mark handled"}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Swimlane({
  title,
  tone,
  orders,
  onSelect,
  bare,
}: {
  title: string;
  tone: "red" | "amber" | "blue" | "green";
  orders: QueueOrderItem[];
  onSelect: (o: QueueOrderItem) => void;
  bare?: boolean;
}) {
  const border =
    tone === "red"
      ? "border-red-light"
      : tone === "amber"
        ? "border-amber-900/50"
        : tone === "blue"
          ? "border-blue-light"
          : "border-green";

  return (
    <section className={bare ? "" : `rounded-lg border ${border} bg-white p-4`}>
      {title ? <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-gray-500">{title}</h2> : null}
      {orders.length === 0 ? (
        <p className="text-sm text-gray-600">No orders.</p>
      ) : (
        <ul className="space-y-2">
          {orders.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                onClick={() => onSelect(o)}
                className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-left text-sm hover:border-gray-200"
              >
                <span className="font-mono text-xs text-gray-500">{o.id.slice(0, 8)}…</span>
                <span className="ml-2 text-gray-700">{o.customerFullName}</span>
                <span className="ml-2 text-gray-500">
                  {o.deliveryCity} {o.deliveryZip}
                </span>
                <span className="float-right text-green">
                  {o.deliveryPaymentMethod === "prepaid" ? PREPAID_VIA_PARTNER_LABEL : formatCentsAsUsd(o.totalCents)}
                </span>
                <div className="mt-1 text-xs text-gray-500">
                  <span className="font-mono">{o.status}</span>
                  {o.locationName ? (
                    <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">
                      {o.locationLabel?.trim() ? o.locationLabel : o.locationName}
                    </span>
                  ) : null}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function OrderDetailBody({
  detail,
  getToken,
  confirmNotes,
  setConfirmNotes,
  onConfirm,
  confirmBusy,
  confirmErr,
}: {
  detail: DispensaryOrderDetailResponse;
  getToken: () => Promise<string | null>;
  confirmNotes: string;
  setConfirmNotes: (s: string) => void;
  onConfirm?: () => void;
  confirmBusy: boolean;
  confirmErr: string | null;
}) {
  const o = detail.order;
  const [receiptMsg, setReceiptMsg] = useState<string | null>(null);
  const [receiptBusy, setReceiptBusy] = useState(false);
  return (
    <div className="mt-4 space-y-4 text-sm">
      <div className="rounded-md border border-gray-200 bg-white p-3 text-xs text-gray-500">
        <p>
          <span className="text-gray-500">Timeline</span> · created{" "}
          <span className="font-mono text-gray-700">{o.createdAt.slice(0, 19)}Z</span>
          {o.confirmedAt ? (
            <>
              {" "}
              · confirmed <span className="font-mono text-gray-700">{o.confirmedAt.slice(0, 19)}Z</span>
            </>
          ) : null}
          {o.cancelledAt ? (
            <>
              {" "}
              · cancelled <span className="font-mono text-gray-700">{o.cancelledAt.slice(0, 19)}Z</span>
            </>
          ) : null}
        </p>
      </div>
      {o.status === "completed" && detail.salesDeliveryStopId ? (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
          {receiptMsg ? <p className="mb-2 text-sm text-amber-800">{receiptMsg}</p> : null}
          <button
            type="button"
            disabled={receiptBusy}
            onClick={() => {
              setReceiptMsg(null);
              setReceiptBusy(true);
              void (async () => {
                const r = await openSalesReceiptPdf(getToken, detail.salesDeliveryStopId!);
                setReceiptBusy(false);
                if (!r.ok) setReceiptMsg(r.message);
              })();
            }}
            className="rounded-md bg-green px-3 py-1.5 text-sm font-medium text-white hover:bg-green-hover disabled:opacity-50"
          >
            {receiptBusy ? "Opening…" : "Download sales receipt (PDF)"}
          </button>
        </div>
      ) : null}
      <p className="text-gray-700">
        {o.customerFullName} · <span className="font-mono text-xs">{o.status}</span>
      </p>
      <p className="text-gray-500">
        {o.deliveryAddressLine1}
        {o.deliveryAddressLine2 ? `, ${o.deliveryAddressLine2}` : ""}
        <br />
        {o.deliveryCity}, {o.deliveryState} {o.deliveryZip}
      </p>
      {o.deliveryInstructions ? (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2"
          data-testid="order-delivery-instructions"
        >
          <p className="text-xs font-medium uppercase tracking-wide text-amber-800">
            Customer delivery note
          </p>
          <p className="mt-1 whitespace-pre-wrap break-words text-gray-700">{o.deliveryInstructions}</p>
        </div>
      ) : null}
      <div className="rounded-md border border-gray-200 px-3 py-2 text-xs text-gray-500">
        <p>
          Purchase limits passed:{" "}
          <span className={o.purchaseLimitsPassed ? "text-green" : "text-red"}>
            {String(o.purchaseLimitsPassed)}
          </span>
          {" · "}
          Load value check:{" "}
          <span className={o.loadValueCheckPassed ? "text-green" : "text-red"}>
            {String(o.loadValueCheckPassed)}
          </span>
        </p>
        <p className="mt-1 font-mono text-gray-500">
          flower oz {o.orderFlowerOz} · conc g {o.orderConcentrateG} · THC mg {o.orderThcMg}
        </p>
      </div>
      <ul className="space-y-2 border-t border-gray-200 pt-3">
        {detail.lineItems.map((li) => (
          <li key={li.id} className="text-gray-500">
            <span className="text-gray-700">{li.productName}</span> × {li.quantity} {li.quantityUnit}
            <div className="font-mono text-xs text-green">METRC {li.metrcPackageUid}</div>
            <div className="text-green">{formatCentsAsUsd(li.lineTotalCents)}</div>
          </li>
        ))}
      </ul>
      <p className="border-t border-gray-200 pt-3 text-gray-700">
        Total <span className="float-right text-green">{formatCentsAsUsd(o.totalCents)}</span>
      </p>
      <p className="text-gray-700">
        Due at door{" "}
        <span className="float-right text-green">
          {o.deliveryPaymentMethod === "prepaid" ? PREPAID_VIA_PARTNER_LABEL : formatCentsAsUsd(o.totalCents)}
        </span>
      </p>
      {onConfirm ? (
        <div className="border-t border-gray-200 pt-4">
          <label className="block text-xs text-gray-500">
            Confirmation notes (optional)
            <textarea
              value={confirmNotes}
              onChange={(e) => setConfirmNotes(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded border border-gray-200 bg-white px-2 py-1 text-sm text-ink"
            />
          </label>
          {confirmErr ? <p className="mt-2 text-sm text-red-300">{confirmErr}</p> : null}
          <button
            type="button"
            disabled={confirmBusy}
            onClick={onConfirm}
            className="mt-3 rounded-md bg-green px-4 py-2 text-sm font-medium text-white hover:bg-green-hover disabled:opacity-50"
          >
            {confirmBusy ? "Confirming…" : "Confirm order"}
          </button>
          <p className="mt-2 text-xs text-gray-600">
            <span className="font-mono">POST …/orders/:orderId/confirm</span>
          </p>
        </div>
      ) : null}
    </div>
  );
}
